import React from 'react'
import { Card, CardHeader, CardBody, CardTitle, Row, Col, Button, Input, FormGroup, Label, FormText } from 'reactstrap'
import { Alert } from '@mui/material'
import {
  ACTIVITY_CANCELLATION_FIELDS,
  DEFAULT_ACTIVITY_CANCELLATION,
  findActivityCancellation,
  upsertActivityCancellation,
  validateActivityCancellation,
} from '../utils/activityCancellation'

// The message participants get when an activity is cancelled automatically (minimum number of
// participants not reached). Stored in config/messagetemplates.cancellations[] as the entry with
// channel 'mail' and collection 'activities' -- see utils/activityCancellation.js for how the backend
// picks and fills it.
class ActivityCancellationMessageTemp extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      loading: true,
      cancellations: [],
      exists: false,
      subject: '',
      body: '',
      replyto: '',
      saved: true,
      saving: false,
      errors: [],
      notice: null,
    }
  }

  componentDidMount() {
    this.unsubscribe = this.props.firebase.getMessageTemplates().onSnapshot((doc) => {
      const cancellations = (doc.exists && Array.isArray(doc.data().cancellations) && doc.data().cancellations) || []
      // keep what the admin is typing; only reload the form when nothing is unsaved
      if (!this.state.saved && !this.state.loading) {
        this.setState({ cancellations })
        return
      }
      const current = findActivityCancellation(cancellations)
      this.setState({
        loading: false,
        cancellations,
        exists: !!current,
        subject: current ? current.subject || '' : DEFAULT_ACTIVITY_CANCELLATION.subject,
        body: current ? current.body || '' : DEFAULT_ACTIVITY_CANCELLATION.body,
        replyto: current ? current.replyto || '' : '',
        // a venue without a template has nothing stored yet: let it save the default right away
        saved: !!current,
      })
    })
  }

  componentWillUnmount() {
    if (this.unsubscribe) this.unsubscribe()
  }

  setField = (key, value) => this.setState({ [key]: value, saved: false, notice: null })

  onSave = async () => {
    const { subject, body, replyto } = this.state
    const errors = validateActivityCancellation({ subject, body, replyto })
    if (errors.length > 0) {
      this.setState({ errors })
      return
    }
    this.setState({ saving: true, errors: [] })
    const changes = { subject: subject.trim(), body, replyto: replyto.trim() || null }
    try {
      await this.props.firebase.addMessageTemplates({
        cancellations: upsertActivityCancellation(this.state.cancellations, changes),
      })
      this.setState({ saving: false, saved: true, exists: true, notice: 'Peruutusviesti tallennettu.' })
    } catch (error) {
      console.error(error)
      this.setState({ saving: false, errors: ['Tallennus epäonnistui: ' + error.message] })
    }
  }

  resetToDefault = () => {
    this.setState({
      subject: DEFAULT_ACTIVITY_CANCELLATION.subject,
      body: DEFAULT_ACTIVITY_CANCELLATION.body,
      saved: false,
      notice: null,
    })
  }

  render() {
    if (this.state.loading) return null
    const { exists, subject, body, replyto, saved, saving, errors, notice } = this.state
    return (
      <Card>
        <CardHeader>
          <CardTitle tag='h5'>Aktiviteetin peruutusviesti</CardTitle>
        </CardHeader>
        <CardBody>
          <p>
            Lähetetään osallistujille sähköpostina, kun aktiviteetti perutaan automaattisesti, koska
            vähimmäisosallistujamäärä ei täyttynyt.
          </p>
          {!exists && (
            <Alert severity='warning' sx={{ mb: 2 }}>
              Peruutusviestiä ei ole tallennettu. Ilman sitä automaattisesti perutun aktiviteetin
              osallistujat eivät välttämättä saa ilmoitusta. Tarkista alla oleva oletusviesti ja tallenna.
            </Alert>
          )}
          <FormGroup>
            <Label for='activityCancellationSubject'>Otsikko</Label>
            <Input id='activityCancellationSubject' value={subject} onChange={(e) => this.setField('subject', e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label for='activityCancellationBody'>Viesti</Label>
            <Input
              id='activityCancellationBody'
              type='textarea'
              rows={8}
              style={{ maxHeight: 'none' }}
              value={body}
              onChange={(e) => this.setField('body', e.target.value)}
            />
            <FormText>Tekstimuotoinen viesti. Rivinvaihdot säilyvät.</FormText>
          </FormGroup>
          <FormGroup>
            <Label for='activityCancellationReplyto'>Vastausosoite (valinnainen)</Label>
            <Input
              id='activityCancellationReplyto'
              type='email'
              value={replyto}
              placeholder='esim. info@seura.fi'
              onChange={(e) => this.setField('replyto', e.target.value)}
            />
          </FormGroup>
          <Label>Dynaamiset kentät</Label>
          {ACTIVITY_CANCELLATION_FIELDS.map((f) => (
            <Row key={f.token}>
              <Col md='4'>&#123;{f.token}&#125;</Col>
              <Col>{f.description}</Col>
            </Row>
          ))}
          <FormText className='mb-3'>Kukin kenttä korvataan viestissä vain kerran (ensimmäinen esiintymä).</FormText>
          {errors.length > 0 && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {errors.map((e) => (
                <div key={e}>{e}</div>
              ))}
            </Alert>
          )}
          {notice && (
            <Alert severity='success' sx={{ mb: 2 }}>
              {notice}
            </Alert>
          )}
          <Row>
            <Col>
              <Button color='primary' disabled={saved || saving} onClick={this.onSave}>
                Tallenna peruutusviesti
              </Button>{' '}
              <Button color='link' disabled={saving} onClick={this.resetToDefault}>
                Palauta oletusviesti
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>
    )
  }
}

export default ActivityCancellationMessageTemp
